import React from "react";
import insightGlyph from "../../../assets/brand/profile/insight-glyph-16.svg";
import { InsightBox } from "./BrandUI";
import { severityClass } from "./charts/chartTokens";
import { safeCopy } from "../../../utils/perfFormat";
import { usePhoneTheme } from "../useIsPhone";

// The payload's insights ({ kind, severity, title, detail }) as V2 copy,
// in payload order, title and detail as given (the backend has already
// rewritten them; perfFormat.safeCopy is only a safety net and leaves clean
// text alone). Each row: a severity dot, the bold title with a period, then
// the detail. Severity dots: high bm-critical, medium bm-attention, info
// bm-info, good bm-positive.
//
//   InsightList({ insights, variant = "read" | "callout" | "cards", label, kindLabels, emptyText, className })
//
// - "read": the profile's insight box (the insight glyph and an overline
//   `label`, "Archetype read") holding the rows; on phones the compact
//   InsightBox with `label` as its title.
// - "callout": the rows in a subtle box under a chart (team cards).
// - "cards": one card per insight with a severity edge, the kind overline
//   (`kindLabels[kind]`) and a tint for "performance_link" (team makeup).
// - Nothing renders for an empty list unless `emptyText` is set: a string
//   becomes one info row (callout / read) or one info card; for "cards" it
//   may also be { kind, title, detail }.
// - The list is a role="list" named by `label` when there is one.

const cx = (...parts) => parts.filter(Boolean).join(" ");

const LINK_KIND = "performance_link";
const EDGE_CLASSES = {
  high: "border-l-bm-critical",
  medium: "border-l-bm-attention",
  info: "border-l-bm-info",
  good: "border-l-bm-positive",
};

const titleWithStop = (title) => {
  const t = safeCopy(title);
  if (!t) return "";
  return /[.!?]$/.test(t) ? t : `${t}.`;
};

// Centred on the first line: bm-body-m (19px lines) on desktop, bm-body-s
// (16px) on phones.
const Dot = ({ severity, phone }) => (
  <span aria-hidden="true" className={cx(phone ? "mt-1" : "mt-[6px]", "h-2 w-2 shrink-0 rounded-full", severityClass(severity))} />
);

const Row = ({ insight, phone }) => {
  const title = titleWithStop(insight.title);
  const detail = safeCopy(insight.detail);
  return (
    <li className={cx("flex items-start gap-2", phone ? "bm-body-s" : "bm-body-m", "text-bm-muted")}>
      <Dot severity={insight.severity} phone={phone} />
      <span className="min-w-0 break-words">
        {title && <strong className="font-semibold text-bm-ink">{title}</strong>}
        {title && detail ? " " : null}
        {detail}
      </span>
    </li>
  );
};

const emptyInsight = (emptyText) =>
  typeof emptyText === "string" ? { kind: "empty", severity: "info", title: "", detail: emptyText } : { severity: "info", ...emptyText };

const Rows = ({ items, label, phone, className = "" }) => (
  // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
  <ul role="list" aria-label={label || undefined} className={cx("flex flex-col", phone ? "gap-2" : "gap-[10px]", className)}>
    {items.map((insight, i) => (
      <Row key={`${insight.kind || "insight"}-${i}`} insight={insight} phone={phone} />
    ))}
  </ul>
);

const ReadBox = ({ items, label, phone, className }) => {
  if (phone) {
    return (
      <InsightBox compact title={label} className={className}>
        <Rows items={items} label={label} phone />
      </InsightBox>
    );
  }
  return (
    <div className={cx("flex flex-col gap-3 rounded-[8px] border border-bm-border bg-white px-5 py-[18px]", className)}>
      {label && (
        <div className="flex items-center gap-[10px]">
          <img src={insightGlyph} alt="" className="h-4 w-4" />
          <span className="bm-overline">{label}</span>
        </div>
      )}
      <Rows items={items} label={label} />
    </div>
  );
};

const Callout = ({ items, label, phone, className }) => (
  <div className={cx("flex flex-col gap-2 rounded-[8px] bg-bm-subtle", phone ? "px-3 py-3" : "px-4 py-3", className)}>
    {label && <span className="bm-overline">{label}</span>}
    <Rows items={items} label={label} phone={phone} className="[&>li+li]:border-t [&>li+li]:border-bm-border [&>li+li]:pt-2" />
  </div>
);

const InsightCard = ({ insight, kindLabels }) => {
  const link = insight.kind === LINK_KIND;
  const kind = kindLabels?.[insight.kind] || insight.kindLabel || null;
  const title = safeCopy(insight.title);
  const detail = safeCopy(insight.detail);
  return (
    <li
      className={cx(
        "flex min-w-0 flex-col gap-[2px] rounded-[8px] border border-l-4 border-bm-border px-[14px] py-3",
        EDGE_CLASSES[insight.severity] || "border-l-bm-tertiary",
        link ? "bg-bm-orange/5" : "bg-white",
      )}
    >
      {kind && <span className={cx("bm-overline", link && "!text-bm-orange")}>{kind}</span>}
      {title && <span className="break-words bm-label-l text-bm-ink">{title}</span>}
      {detail && <span className="break-words bm-body-s text-bm-muted">{detail}</span>}
    </li>
  );
};

const Cards = ({ items, label, kindLabels, phone, className }) => (
  // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
  <ul
    role="list"
    aria-label={label || undefined}
    className={cx(phone ? "flex flex-col gap-[10px]" : "grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[10px]", className)}
  >
    {items.map((insight, i) => (
      <InsightCard key={`${insight.kind || "insight"}-${i}`} insight={insight} kindLabels={kindLabels} />
    ))}
  </ul>
);

export const InsightList = ({ insights, variant = "read", label, kindLabels, emptyText, className = "" }) => {
  const phone = usePhoneTheme();
  const list = Array.isArray(insights) ? insights.filter((i) => i && (i.title || i.detail)) : [];
  const items = list.length ? list : emptyText ? [emptyInsight(emptyText)] : [];
  if (!items.length) return null;
  if (variant === "cards") return <Cards items={items} label={label} kindLabels={kindLabels} phone={phone} className={className} />;
  if (variant === "callout") return <Callout items={items} label={label} phone={phone} className={className} />;
  return <ReadBox items={items} label={label} phone={phone} className={className} />;
};

export default InsightList;
